import React, { useContext, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayouts from "../../layout/MainLayouts";
// Accreditation import removed — section commented out below until accreditation
// claims are confirmed; re-add from "../LandingPage/Component/Accreditation" if it comes back.
import client from "../../api/client";
import { GlobalContext } from "../../context";
import { getUserCountry, getFormattedPrice } from "../../utils/currency";
import { getCache, setCache } from "../../utils/cache";

function StarRating({ rating = 0, max = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < Math.floor(rating);
        const half   = !filled && i < rating;
        return (
          <svg key={i} viewBox="0 0 24 24" className="w-4 h-4" fill={filled ? "#d15424" : half ? "url(#half)" : "none"} stroke="#d15424" strokeWidth="1.5">
            {half && (
              <defs>
                <linearGradient id="half">
                  <stop offset="50%" stopColor="#d15424" />
                  <stop offset="50%" stopColor="transparent" />
                </linearGradient>
              </defs>
            )}
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
        );
      })}
    </div>
  );
}

export default function CourseDetailV2() {
  const { uuid } = useParams();
  const navigate  = useNavigate();
  const { authState: { loggedIn } } = useContext(GlobalContext);

  const [course,    setCourse]    = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const country  = await getUserCountry();
        const cacheKey = `course_detail_v2_${uuid}`;

        const map = (c) => ({
          id:              c.id,
          slug:            c.slug,
          title:           c.title,
          desc:            c.description || c.what_you_will_learn || "",
          whatYouWillLearn: c.what_you_will_learn || null,
          programFeatures:  c.program_features   || null,
          programTarget:    c.program_target      || null,
          programPrereq:    c.program_prerequisite || null,
          programBenefits:  c.program_benefits    || null,
          difficulty:       c.difficulty_level    || "Beginner",
          price:            getFormattedPrice(c, country),
          priceNgn:         c.price_ngn           || 0,
          priceUsd:         c.price_usd           || 0,
          priceSymbol:      country === "NG" ? "₦" : "$",
          numericPrice:     country === "NG" ? (c.price_ngn || 0) : (c.price_usd || 0),
          duration:         c.duration            || null,
          image:            c.image_url           || "/default-course.jpg",
          coverImage:       c.cover_image_url     || c.image_url || "/default-course.jpg",
          brochureUrl:      c.brochure_url         || null,
          format:           c.course_type_id === 1 ? "Live" : "Online",
          isLive:           c.course_type_id === 1,
          modules:          Array.isArray(c.curriculum) && c.curriculum.length > 0
            ? c.curriculum.map((m) => {
                const topics = Array.isArray(m.topics)
                  ? m.topics.map((t) => (typeof t === "object" ? t.topic : t)).filter(Boolean)
                  : [];
                const parts = [];
                if (m.objective) parts.push(`<p><em>Objective:</em> ${m.objective}</p>`);
                if (topics.length) parts.push(`<ul>${topics.map((t) => `<li>${t}</li>`).join("")}</ul>`);
                if (m.lab) parts.push(`<p><strong>Practical Lab:</strong> ${m.lab}</p>`);
                if (m.assessment) parts.push(`<p><strong>Assessment:</strong> ${m.assessment}</p>`);
                return { title: m.title || "Module", desc: parts.join("") };
              })
            : (c.modules || []).map((m) => ({
                title: m.name || m.title || "Module",
                desc:  m.description  || "",
              })),
          skills:           Array.isArray(c.skills)
            ? c.skills.map((s) => (typeof s === "object" ? s.skill || s.name || s.title || "" : s)).filter(Boolean)
            : [],
          careerOpportunities: Array.isArray(c.career_opportunities)
            ? c.career_opportunities.map((o) => (typeof o === "object" ? o.opportunity || o.name || o.title || "" : o)).filter(Boolean)
            : [],
          salaryTrend:      c.salary_trend         || null,
          careerPathways:   Array.isArray(c.career_pathways)
            ? c.career_pathways.map((p) => (typeof p === "object" ? p.pathway || p.name || p.title || "" : p)).filter(Boolean)
            : [],
          learningOutcomes: Array.isArray(c.learning_outcomes)
            ? c.learning_outcomes.map((o) => (typeof o === "object" ? o.outcome || o.name || o.title || "" : o)).filter(Boolean)
            : [],
          isSubscribed:     c.current_user_has_subscribed       || false,
          category:         c.category             || null,
          reviews:          c.reviews              || [],
          reviewsInfo:      c.reviews_info         || null,
        });

        const cached = getCache(cacheKey);
        if (cached) { setCourse(map(cached)); return; }

        const res = await client.get(`/v2/courses/${uuid}`);
        const c   = res.data.data || res.data;
        if (c) { setCache(cacheKey, c); setCourse(map(c)); }
      } catch (err) {
        console.error("Error fetching course detail", err);
      }
    };
    load();
  }, [uuid]);

  if (!course) {
    return (
      <MainLayouts>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayouts>
    );
  }

  const handleEnroll = () => {
    if (!loggedIn) { navigate("/sign-in"); return; }
    if (course.isSubscribed) { navigate("/dashboard"); return; }
    if (course.isLive) {
      navigate("/cohorts", { state: { course: { slug: course.slug, title: course.title } } });
    } else {
      navigate(`/payment`, { state: { course } });
    }
  };

  const fmtNum = (n) => Number(n).toLocaleString();

  const heroDesc = (course.desc || "").replace(/<[^>]*>?/gm, "").trim();
  const heroSnippet = heroDesc.split(" ").slice(0, 35).join(" ") + (heroDesc.split(" ").length > 35 ? "…" : "");

  return (
    <MainLayouts>
      <div className="bg-white min-h-screen pb-24 font-sans text-gray-900">

        {/* ── HERO ── */}
        <div className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img src={course.coverImage} alt={course.title} className="w-full h-full object-cover opacity-40 grayscale" />
            <div className="absolute inset-0 bg-gray-900/80" />
          </div>

          <div className="relative z-10 max-w-[1560px] mx-auto px-6">
            <button
              onClick={() => navigate(-1)}
              className="mb-10 w-12 h-12 border border-white/20 hover:bg-primary flex items-center justify-center text-white transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="max-w-5xl">
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="px-4 py-2 border border-primary text-primary text-xs font-bold uppercase tracking-widest">
                  {course.format} Format
                </span>
                <span className="px-4 py-2 border border-white/30 text-white text-xs font-bold uppercase tracking-widest">
                  {course.difficulty}
                </span>
                {course.category && (
                  <span className="px-4 py-2 border border-white/30 text-white text-xs font-bold uppercase tracking-widest">
                    {course.category}
                  </span>
                )}
              </div>

              <h1 className="text-white text-4xl md:text-6xl lg:text-[5rem] font-black mb-8 leading-[1.1] tracking-tight">
                {course.title}<span className="text-primary">.</span>
              </h1>

              {heroSnippet && (
                <p className="text-gray-300 text-xl md:text-2xl leading-relaxed max-w-4xl font-light">
                  {heroSnippet}
                </p>
              )}

              {course.reviewsInfo && course.reviewsInfo.total_reviews > 0 && (
                <div className="mt-6 flex items-center gap-3">
                  <StarRating rating={parseFloat(course.reviewsInfo.average_rating) || 0} />
                  <span className="text-white font-bold">{Number(course.reviewsInfo.average_rating).toFixed(1)}</span>
                  <span className="text-gray-400 text-sm">({course.reviewsInfo.total_reviews} review{course.reviewsInfo.total_reviews !== 1 ? "s" : ""})</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── FEATURE BAR ── */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-[1560px] mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-gray-100 py-10">
              {[
                { title: "Beginner Friendly",     sub: "No prior experience required." },
                { title: "Job Placement Support", sub: "Internships & job placements." },
                { title: "Live & On-Demand",      sub: "Attend live classes and watch recordings." },
                { title: "Expert Mentorship",     sub: "Learn from industry experts." },
                { title: "Certificate",           sub: "Earn a recognised certificate." },
              ].map((f) => (
                <div key={f.title} className="px-6 text-center md:text-left first:pl-0">
                  <h4 className="text-gray-900 font-bold text-sm md:text-base mb-2">{f.title}</h4>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── MAIN BODY ── */}
        <div className="max-w-[1560px] mx-auto px-6 pt-16 lg:pt-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-20">

            {/* LEFT COLUMN */}
            <div className="lg:col-span-8 space-y-20">

              {/* Mobile secondary CTAs (sidebar is hidden below lg) */}
              <div className="lg:hidden flex flex-col sm:flex-row gap-3">
                {course.brochureUrl && (
                  <a
                    href={course.brochureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex-1 py-4 bg-white border border-gray-300 rounded-xl text-gray-900 hover:bg-gray-50 font-bold text-base transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Brochure
                  </a>
                )}
                <button
                  onClick={() => window.open("https://app.e1xacademy.net/pioneer-cohort", "_blank", "noopener,noreferrer")}
                  className="flex-1 py-4 border border-primary text-primary font-bold text-base hover:bg-primary/5 rounded-xl transition-colors duration-300"
                >
                  Apply for Scholarship
                </button>
              </div>

              {/* Course Overview */}
              <section>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Course Overview</h2>
                <div className="bg-gray-50 p-10 md:p-14">
                  <div
                    className="text-gray-800 leading-loose text-lg font-light prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: course.desc }}
                  />
                </div>
              </section>

              {/* What You Will Learn (distinct from description) */}
              {course.whatYouWillLearn && course.whatYouWillLearn !== course.desc && (
                <section>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">What You Will Learn</h2>
                  <div className="bg-gray-50 p-10 md:p-14">
                    <div
                      className="text-gray-800 leading-loose text-lg font-light prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: course.whatYouWillLearn }}
                    />
                  </div>
                </section>
              )}

              {/* Who This Is For + Prerequisites */}
              {(course.programTarget || course.programPrereq) && (
                <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {course.programTarget && (
                    <div className="bg-primary/5 p-8 border-l-4 border-primary">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Who Is This For?</h3>
                      <div className="text-gray-700 text-base leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: course.programTarget }} />
                    </div>
                  )}
                  {course.programPrereq && (
                    <div className="bg-gray-50 p-8 border-l-4 border-gray-300">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Prerequisites</h3>
                      <div className="text-gray-700 text-base leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: course.programPrereq }} />
                    </div>
                  )}
                </section>
              )}

              {/* Skills Covered */}
              {course.skills.length > 0 && (
                <section>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Skills You'll Gain</h2>
                  <div className="flex flex-wrap gap-3">
                    {course.skills.map((skill, i) => (
                      <span key={i} className="px-5 py-2.5 border border-gray-200 text-gray-800 text-sm font-semibold rounded-full hover:border-primary hover:text-primary transition-colors">
                        {skill}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Hands-on Projects */}
              <section>
                <div className="bg-primary/5 p-10 md:p-14 border-l-4 border-primary">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-primary shrink-0">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                    </svg>
                    Hands-on Projects & Assessments
                  </h3>
                  {course.programFeatures ? (
                    <div className="text-gray-800 text-lg font-light prose max-w-none" dangerouslySetInnerHTML={{ __html: course.programFeatures }} />
                  ) : (
                    <p className="text-gray-800 leading-relaxed text-lg font-light">
                      Throughout your period of learning, you will engage in continuous practical assessments and build real-world projects. This ensures you can apply what you learn immediately and graduate with a robust portfolio of work.
                    </p>
                  )}
                </div>
              </section>

              {/* Learning Outcomes */}
              {course.learningOutcomes.length > 0 && (
                <section>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10">Learning Outcomes</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                    {course.learningOutcomes.map((outcome, idx) => (
                      <div key={idx} className="flex items-start gap-5">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="w-5 h-5 text-primary shrink-0 mt-1">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-800 font-medium text-lg leading-relaxed">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Curriculum */}
              {course.modules.length > 0 && (
                <section>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Curriculum</h2>
                  <div className="border-t border-gray-200">
                    {course.modules.map((module, index) => (
                      <div key={index} className="border-b border-gray-200">
                        <button
                          onClick={() => setActiveTab(activeTab === index ? null : index)}
                          className="w-full py-8 flex items-center justify-between text-left hover:bg-gray-50 px-4 transition-colors"
                        >
                          <div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mr-3">Module {index + 1}</span>
                            <h3 className={`font-bold text-xl inline ${activeTab === index ? "text-primary" : "text-gray-900"}`}>
                              {module.title}
                            </h3>
                          </div>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`w-5 h-5 shrink-0 transition-transform duration-300 ${activeTab === index ? "text-primary rotate-180" : "text-gray-400"}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <div className={`px-4 overflow-hidden transition-all duration-500 ease-in-out ${activeTab === index ? "max-h-[600px] pb-8 opacity-100" : "max-h-0 opacity-0"}`}>
                          {module.desc ? (
                            <div className="text-gray-700 leading-relaxed text-lg font-light prose max-w-none pt-2" dangerouslySetInnerHTML={{ __html: module.desc }} />
                          ) : (
                            <p className="text-gray-400 italic pt-2">No description available for this module.</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Career Outcomes */}
              {(course.careerOpportunities.length > 0 || course.salaryTrend || course.careerPathways.length > 0) && (
                <section>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Career Outcomes</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {course.careerOpportunities.length > 0 && (
                      <div className="p-10 bg-gray-50">
                        <h3 className="text-sm font-bold text-gray-500 mb-6 uppercase tracking-widest">Career Opportunities</h3>
                        <div className="flex flex-col gap-5">
                          {course.careerOpportunities.map((opp, idx) => (
                            <span key={idx} className="text-gray-900 text-xl font-medium">{opp}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {course.salaryTrend && (
                      <div className="bg-primary/5 p-10 flex flex-col justify-center">
                        <h3 className="text-sm font-bold text-gray-500 mb-4 uppercase tracking-widest">Salary Trend</h3>
                        <div className="text-3xl font-bold text-gray-800 mb-3">{course.salaryTrend}</div>
                        <p className="text-gray-600 text-sm font-medium">Based on national industry averages.</p>
                      </div>
                    )}

                    {course.careerPathways.length > 0 && (
                      <div className="md:col-span-2 bg-gray-50 border border-gray-100 p-10">
                        <h3 className="text-sm font-bold text-gray-500 mb-6 uppercase tracking-widest">Career Pathways</h3>
                        <div className="space-y-6">
                          {course.careerPathways.map((path, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-3 font-medium text-gray-800 text-lg">
                              {path.split("→").map((step, sIdx, arr) => (
                                <React.Fragment key={sIdx}>
                                  <span className={sIdx === arr.length - 1 ? "text-primary font-bold" : ""}>{step.trim()}</span>
                                  {sIdx < arr.length - 1 && (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Program Benefits */}
              {course.programBenefits && (
                <section>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Program Benefits</h2>
                  <div className="bg-gray-50 p-10 md:p-14">
                    <div className="text-gray-800 leading-loose text-lg font-light prose max-w-none" dangerouslySetInnerHTML={{ __html: course.programBenefits }} />
                  </div>
                </section>
              )}

              {/* Student Reviews */}
              {course.reviews.length > 0 && (
                <section>
                  <div className="flex items-end justify-between mb-10">
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">Student Reviews</h2>
                    {course.reviewsInfo && (
                      <div className="text-right hidden md:block">
                        <div className="text-5xl font-black text-gray-900">{Number(course.reviewsInfo.average_rating).toFixed(1)}</div>
                        <StarRating rating={parseFloat(course.reviewsInfo.average_rating)} />
                        <p className="text-sm text-gray-500 mt-1">{course.reviewsInfo.total_reviews} review{course.reviewsInfo.total_reviews !== 1 ? "s" : ""}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-6">
                    {course.reviews.slice(0, 6).map((review, idx) => (
                      <div key={idx} className="bg-gray-50 p-8">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm shrink-0">
                              {(review.user?.firstname?.[0] || review.name?.[0] || "S").toUpperCase()}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{review.user?.firstname || review.name || "Student"}</p>
                              {review.created_at && (
                                <p className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                              )}
                            </div>
                          </div>
                          {review.stars && <StarRating rating={review.stars} />}
                        </div>
                        {review.review && <p className="text-gray-700 leading-relaxed">{review.review}</p>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Payment Plans */}
              {course.numericPrice > 0 && (
                <section className="mb-16">
                  <div className="bg-[#0b2447] rounded-xl p-10 md:p-14 text-center">
                    <h2 className="text-3xl font-bold text-white mb-10">Flexible and Affordable Payment</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {[
                        { label: "Pay in 6 Installments", amount: course.numericPrice / 6 },
                        { label: "Pay in 3 Installments", amount: course.numericPrice / 3 },
                        { label: "Full Payment",           amount: course.numericPrice },
                      ].map(({ label, amount }) => (
                        <div key={label} className="bg-white rounded-xl p-8 shadow-sm flex flex-col">
                          <p className="text-sm font-bold text-[#0b2447] mb-2">{label}</p>
                          <p className="text-3xl font-black text-gray-900 mb-6">
                            {course.priceSymbol}{fmtNum(Math.round(amount))}
                          </p>
                          <div className="mt-auto">
                            <button
                              onClick={handleEnroll}
                              className="w-full py-3 border border-[#0b2447] text-[#0b2447] hover:bg-[#0b2447] hover:text-white font-bold text-sm rounded-lg transition-colors"
                            >
                              Enrol Now
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

            </div>

            {/* RIGHT COLUMN: Sticky Sidebar */}
            <div className="hidden lg:block lg:col-span-4 relative">
              <div className="sticky top-32 space-y-6">

                <div className="bg-white rounded-2xl p-10 shadow-[0_10px_40px_rgba(0,0,0,0.06)] border border-gray-100">
                  <div className="text-center mb-10">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Tuition Fee</div>
                    <div className="text-5xl lg:text-6xl font-black text-gray-900 mb-1">{course.price}</div>
                    {course.reviewsInfo && course.reviewsInfo.total_reviews > 0 && (
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <StarRating rating={parseFloat(course.reviewsInfo.average_rating)} />
                        <span className="text-sm text-gray-500">{Number(course.reviewsInfo.average_rating).toFixed(1)} ({course.reviewsInfo.total_reviews})</span>
                      </div>
                    )}
                    <p className="text-gray-500 text-sm mt-3">Flexible payment plans available.</p>
                  </div>

                  <button
                    onClick={handleEnroll}
                    className="w-full py-5 bg-primary hover:bg-[#b04233] text-white font-bold text-xl rounded-xl transition-colors duration-300 mb-4"
                  >
                    {course.isSubscribed ? "Go to Dashboard" : "Enroll Now"}
                  </button>

                  {course.brochureUrl && (
                    <a
                      href={course.brochureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="w-full py-4 bg-white border border-gray-300 rounded-xl text-gray-900 hover:bg-gray-50 hover:border-gray-400 font-bold text-base transition-all duration-300 mb-4 flex items-center justify-center gap-3"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Brochure
                    </a>
                  )}

                  <button
                    onClick={() => window.open("https://app.e1xacademy.net/pioneer-cohort", "_blank", "noopener,noreferrer")}
                    className="w-full py-4 text-primary font-bold text-base hover:bg-primary/5 rounded-xl transition-colors duration-300"
                  >
                    Apply for Scholarship
                  </button>
                </div>

                <div className="bg-gray-50 p-10 rounded-2xl">
                  <h4 className="font-bold text-gray-900 text-xl mb-8">Program Highlights</h4>
                  <ul className="space-y-6">
                    {course.duration && (
                      <li className="flex items-start gap-4">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-primary shrink-0 mt-0.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Duration</p>
                          <p className="font-bold text-gray-900 text-lg">{course.duration}</p>
                        </div>
                      </li>
                    )}
                    <li className="flex items-start gap-4">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-primary shrink-0 mt-0.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Certificate</p>
                        <p className="font-bold text-gray-900 text-lg">Industry Recognised</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-primary shrink-0 mt-0.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Format</p>
                        <p className="font-bold text-gray-900 text-lg">{course.format} Learning</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-primary shrink-0 mt-0.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-widest mb-1">Difficulty</p>
                        <p className="font-bold text-gray-900 text-lg">{course.difficulty}</p>
                      </div>
                    </li>
                  </ul>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Mobile Sticky CTA */}
        <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-100 p-4 z-50 lg:hidden flex items-center justify-between gap-4 shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
          <div className="shrink-0">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Tuition</div>
            <div className="text-2xl font-black text-gray-900">{course.price}</div>
          </div>
          <button onClick={handleEnroll} className="flex-grow py-4 bg-primary text-white font-bold text-base rounded-xl">
            {course.isSubscribed ? "Go to Dashboard" : "Enroll Now"}
          </button>
        </div>

      </div>

      {/* <Accreditation /> — commented out until accreditation claims are confirmed */}
    </MainLayouts>
  );
}
